// How many establishments a person runs, split by state.
//
// Two numbers rather than one because a lone total is ambiguous: an owner with five closed
// restaurants and an owner with five open ones are not the same prospect, and the old single
// count could not tell them apart. Active is the number that drives prospecting (and the
// "Nb établissements" buckets, and the multi-site rule); closed only matters on the Fermetures
// page, where what an operator has shut is the subject of the page.
export interface FounderCounts {
    active: number;
    closed: number;
    /** Franchise ids of their open sites; empty for a franchisor's executive (> 80 franchised sites). */
    franchises?: string[];
}

/**
 * The line printed under a contact's name.
 *
 * Outside the Fermetures folder this only ever prints the active count. Falling back to the
 * closed one made a row read "12 établissements fermés" for an operator still running 48
 * places — the closure of a single restaurant said nothing about the rest of their portfolio,
 * and the line was taken as "everything this person had is shut". Printing nothing is honest;
 * printing the closed count outside the page that is about closures is not.
 *
 * Returns null while the counts are still in flight: rendering nothing is better than rendering
 * a number that is about to change.
 */
export function founderCountLabel(
    counts: FounderCounts | undefined,
    prefer: "active" | "closed" = "active"
): string | null {
    if (!counts) return null;

    const { active, closed } = counts;

    const activeLabel = active > 0
        ? `${active} établissement${active > 1 ? "s" : ""} actif${active > 1 ? "s" : ""}`
        : null;

    // Closures context (the Fermetures folder): lead with what they shut, since that is what
    // the page is about and what its filters are grading. The active count is kept alongside
    // it whenever there is one, so a closure on a 48-restaurant operator never reads as an
    // operator who has wound everything down.
    if (prefer === "closed" && closed > 0) {
        const closedLabel = `${closed} établissement${closed > 1 ? "s" : ""} fermé${closed > 1 ? "s" : ""}`;
        return active > 0
            ? `${closedLabel} · ${active} actif${active > 1 ? "s" : ""}`
            : closedLabel;
    }

    return activeLabel;
}
